import React, { useRef, useState, useMemo } from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { Download, Edit3 } from "lucide-react";
import { marked } from "marked";
import API_Root from "../Constants/API_Root";
import { authFetch } from "../Authentication/authService";

export default function AppCanvas({
  canvasMessage = [],
  setOpenModule,
  responseType,
}) {
  const canvasRef = useRef(null);
  const [isEditing, setIsEditing] = useState(true);

  // ✅ ALWAYS get the latest answer from mixed response array
  const response = useMemo(() => {
    if (!Array.isArray(canvasMessage)) return "";

    const lastAnsweredItem = [...canvasMessage]
      .reverse()
      .find((item) => typeof item?.answer === "string");

    return lastAnsweredItem?.answer || "";
  }, [canvasMessage]);

  // ✅ Markdown → HTML (safe)
  const promptHtml = useMemo(() => {
    return marked.parse(response || "");
  }, [response]);

  // ✅ DOC download handler
  const handleDownload = async () => {
    // Find latest ESAP response
    const latestEsap = [...canvasMessage]
      .reverse()
      .find((item) => item?.type === "esap" && item?.data?.grouped_results);

    // ESAP Excel download
    if (latestEsap) {
      try {
        const payload = {
          report_name: latestEsap?.data?.report_name,
          grouped_results: latestEsap?.data?.grouped_results,
        };

        const response = await authFetch(
          `${API_Root}/files/generate-excel-report`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to generate report");
        }

        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = latestEsap?.data?.report_name || "ESAP_Report.xlsx";

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);

        return;
      } catch (error) {
        console.error("ESAP download error:", error);
        return;
      }
    }

    // Existing ESDD logic
    // Find latest ESDD response
    const latestEsdd = [...canvasMessage]
      .reverse()
      .find((item) => item?.type === "esdd" && item?.answer);

    // ESDD DOCX download
    if (latestEsdd) {
      try {
        const payload = {
          report: latestEsdd.answer,
        };

        const response = await authFetch(
          `${API_Root}/files/generate-dd-report`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to generate DD report");
        }

        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "ESDD_Report.docx";

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);

        return;
      } catch (error) {
        console.error("ESDD download error:", error);
        return;
      }
    }

    // Existing DOC download logic
    if (!canvasRef.current) return;

    const htmlContent = canvasRef.current.innerHTML;

    const docHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8" />
          <title>Canvas Content</title>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;

    const blob = new Blob([docHtml], {
      type: "application/msword",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "canvas-content.doc";
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <Container fluid style={{ padding: "7px", background: "#f5f7fb" }}>
      <Row className="justify-content-center">
        <Col md={12}>
          <Card
            style={{
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            }}
          >
            <Card.Header
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#ffffff",
                borderBottom: "1px solid #e5e7eb",
                borderRadius: "16px 16px 0 0",
              }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setIsEditing((prev) => !prev)}
                >
                  <Edit3 size={16} style={{ marginRight: "4px" }} />
                  {isEditing ? "Lock" : "Edit"}
                </Button>

                <Button variant="primary" size="sm" onClick={handleDownload}>
                  <Download size={16} style={{ marginRight: "4px" }} />
                  Download
                </Button>
              </div>
            </Card.Header>

            <Card.Body
              style={{
                minHeight: "320px",
                background: "#ffffff",
                padding: "20px",
                width: "fit-content",
              }}
            >
              {/* ✅ SINGLE editable canvas */}
              <div
                ref={canvasRef}
                contentEditable={isEditing}
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: promptHtml }}
                style={{
                  minHeight: "280px",
                  outline: "none",
                  fontSize: "16px",
                  lineHeight: "1.6",
                  cursor: isEditing ? "text" : "default",
                  whiteSpace: "normal",
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
